import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { Phone, PhoneForwarded, Truck, MessageCircle, Info, ShieldAlert, BellRing, ListTree, Wallet, Users, MapPin, PhoneMissed, Plus, Trash2 } from 'lucide-react'
import { SettingsCrumb } from '../admin/SettingsCrumb.js'
import { adminMutate, apiRead } from '../admin/primitives.js'
import { Checkbox, SwitchRow } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { Link } from '../router.js'
import {
  BORNES, EQUIPES, MOMENTS, ORDRE_EQUIPES, ORDRE_TYPES_TRANSFERT, SONNETTES, TYPES, choixTelephones, depuisSaisie, erreursChamps, invalide, lignes,
  messageErreur, quandParis, versSaisie,
  type Equipe, type ReglagesSacha, type ReponseReglages, type SaisieReglages, type TypeTransfert,
} from './interphone-model.js'

/**
 * ADMINISTRATION › RÉGLAGES › INTERPHONE (`/settings/interphone`, 22/10/2026).
 *
 * Les réglages de Sacha appartiennent au service Interphone : cette page les
 * LIT chez lui et les lui RENVOIE en entier (`PUT /api/v1/interphone/reglages`),
 * sans rien garder. Une seule Save Bar pour toute la page.
 *
 *  · Le serveur dit si la page est modifiable (`modifiable` + `raison` : Mode
 *    sûr de l'interphone, droit de régler) ; la raison s'affiche en haut.
 *  · Concurrence : le `maj` lu part avec l'enregistrement ; si quelqu'un a
 *    modifié entre-temps, le serveur répond 409 « modifié par … entre-temps ».
 *  · Après un enregistrement, l'écran prend la RÉPONSE du serveur (réglages
 *    appliqués, nouveau `maj`) — pas une relecture lancée sans l'attendre.
 *  · Le service valide : son refus est affiché tel qu'il l'écrit ; les bornes
 *    connues sont dites près du champ concerné avant l'envoi.
 */

type Lecture = { etat: 'chargement' } | { etat: 'ok'; data: ReponseReglages } | { etat: 'erreur'; message: string }

export function InterphoneReglagesPage() {
  const [l, setL] = useState<Lecture>({ etat: 'chargement' })
  const charger = useCallback(() => {
    void apiRead<ReponseReglages>('/api/v1/interphone/reglages')
      .then((r) => setL(r.status === 200 && r.data?.reglages ? { etat: 'ok', data: r.data } : { etat: 'erreur', message: messageErreur(r.status, r.data, 'Les réglages de Sacha sont indisponibles.') }))
      .catch(() => setL({ etat: 'erreur', message: 'Gestion n’a pas répondu.' }))
  }, [])
  useEffect(() => { charger() }, [charger])

  const appliquer = (r: { reglages: ReglagesSacha; maj: string | null; par: string }) =>
    setL((x) => (x.etat === 'ok' ? { etat: 'ok', data: { ...x.data, reglages: r.reglages, maj: r.maj, par: r.par } } : x))

  return (
    <main className="lc-page lc-page--readable lc-stagger ip-page">
      <SettingsCrumb />
      <h1 className="lc-title">Interphone IA</h1>
      <p className="lc-subtitle">
        Ce que fait Sacha quand l’interphone de la porte du personnel sonne : répondre ou orienter vers le Répertoire,
        quelle entrée du Répertoire faire sonner, comment elle se présente, et le budget mensuel de l’IA. Le journal est dans{' '}
        <Link href="/securite/interphone-ia" className="ip-lien">Sécurité › Interphone IA</Link>.
      </p>
      {l.etat === 'chargement' ? <p className="lc-hint" aria-busy="true">Lecture des réglages de Sacha…</p>
        : l.etat === 'erreur' ? <p className="ip-gris" role="status">{l.message}</p>
          : <Formulaire key={l.data.maj ?? 'defauts'} data={l.data} onEnregistre={appliquer} onRecharger={charger} />}
    </main>
  )
}

function Formulaire({ data, onEnregistre, onRecharger }: {
  data: ReponseReglages
  onEnregistre: (r: { reglages: ReglagesSacha; maj: string | null; par: string }) => void
  onRecharger: () => void
}) {
  // Le serveur décide ; un ancien serveur sans le champ laisse la page modifiable (il refusera lui-même).
  const peutModifier = data.modifiable !== false
  const [s, setS] = useState<SaisieReglages>(() => versSaisie(data.reglages))
  const [conflit, setConflit] = useState(false)
  const maj = (p: Partial<SaisieReglages>) => setS((x) => ({ ...x, ...p }))

  const serveur = useMemo(() => JSON.stringify(depuisSaisie(versSaisie(data.reglages), data.reglages)), [data.reglages])
  const dirty = peutModifier && JSON.stringify(depuisSaisie(s, data.reglages)) !== serveur
  const raison = invalide(s)
  const erreurs = erreursChamps(s)

  useSaveBar({
    dirty,
    invalid: raison,
    successMessage: 'Réglages de Sacha enregistrés.',
    onSave: async () => {
      const r = await adminMutate<{ error?: string; message?: string; reglages?: ReglagesSacha; maj?: string | null; par?: string }>(
        'PUT', '/api/v1/interphone/reglages', { reglages: depuisSaisie(s, data.reglages), maj: data.maj })
      if (r.status >= 200 && r.status < 300 && r.data.reglages) {
        onEnregistre({ reglages: r.data.reglages, maj: r.data.maj ?? null, par: r.data.par ?? data.par })
        return true
      }
      if (r.data.error === 'STEPUP_REQUIRED') return 'Confirmation d’identité annulée : rien n’a été enregistré.'
      if (r.data.error === 'REGLAGES_MODIFIES') setConflit(true)
      return messageErreur(r.status, r.data, 'Le service Interphone a refusé ces réglages.')
    },
    onCancel: () => setS(versSaisie(data.reglages)),
  })

  const basculer = (liste: string[], tel: string, oui: boolean): string[] =>
    oui ? (liste.includes(tel) ? liste : [...liste, tel]) : liste.filter((x) => x !== tel)
  const majEquipe = (e: Equipe, tel: string, oui: boolean) =>
    setS((x) => ({ ...x, routage: { ...x.routage, [e]: basculer(x.routage[e], tel, oui) } }))

  const desactive = !peutModifier
  const telephones = Array.isArray(data.telephones) ? data.telephones : []
  const rep = s.repertoire
  const majRep = (p: Partial<NonNullable<SaisieReglages['repertoire']>>) =>
    setS((x) => (x.repertoire ? { ...x, repertoire: { ...x.repertoire, ...p } } : x))

  return (
    <>
      {data.raison ? (
        <p className="ip-alerte" role="status"><ShieldAlert size={16} aria-hidden="true" /><span>{data.raison}</span></p>
      ) : null}
      {conflit ? (
        <p className="ip-alerte" role="alert">
          <ShieldAlert size={16} aria-hidden="true" />
          <span>Quelqu’un a modifié ces réglages entre-temps.{' '}
            <button type="button" className="lc-btn lc-btn--ghost" onClick={onRecharger}>Recharger les réglages</button>
          </span>
        </p>
      ) : null}
      {data.maj ? <p className="lc-hint">Dernière modification {quandParis(data.maj)}{data.par ? ` par ${data.par}` : ''}.</p> : null}

      <section className="lc-panel ip-section">
        <div className="lc-panel__body">
          <SwitchRow checked={s.mode_test} disabled={desactive} onChange={(v) => maj({ mode_test: v })}
            title="Mode test"
            desc={s.mode_test
              ? 'Sacha répond et note tout, mais ne fait sonner personne. À couper avant de compter sur elle.'
              : s.repertoire
                ? 'Sacha fait réellement sonner l’interphone sur l’entrée du Répertoire choisie (et les téléphones SIP pour les appels de test).'
                : 'Sacha appelle réellement les téléphones ci-dessous.'} />
        </div>
      </section>

      {s.sonnette ? (
        <section className="lc-panel ip-section" aria-labelledby="ip-sonnette">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-sonnette"><BellRing size={16} aria-hidden="true" /> Sonnette de la porte du personnel</h2></header>
          <div className="lc-panel__body ip-reglages">
            <fieldset className="ip-groupe" aria-describedby="ip-sonnette-aide">
              <legend className="ip-groupe__titre">Quand on appuie sur la sonnette</legend>
              <p className="lc-hint" id="ip-sonnette-aide">
                Le Répertoire et le bouton Livraison de l’interphone ne changent pas : ils sonnent toujours directement.
                Si Sacha ne répond pas (PC éteint), le message « Répertoire » est joué à sa place.
              </p>
              <div className="ip-choix" role="radiogroup" aria-label="Mode de la sonnette">
                {SONNETTES.map((o) => (
                  <label key={o.cle} className={`ip-choix__option${s.sonnette === o.cle ? ' ip-choix__option--actif' : ''}`}>
                    <input type="radio" name="ip-sonnette" value={o.cle} checked={s.sonnette === o.cle} disabled={desactive}
                      onChange={() => maj({ sonnette: o.cle })} />
                    <span className="ip-choix__texte"><strong>{o.libelle}</strong><span className="lc-hint">{o.aide}</span></span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        </section>
      ) : null}

      {rep ? (
        <section className="lc-panel ip-section" aria-labelledby="ip-repertoire">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-repertoire"><ListTree size={16} aria-hidden="true" /> Transfert vers le Répertoire UniFi</h2></header>
          <div className="lc-panel__body ip-reglages">
            <p className="lc-hint">
              Quand Sacha prévient quelqu’un, l’interphone appelle l’entrée de son Répertoire choisie ici : tous ses destinataires
              (appli UniFi, écrans, portables) sonnent, et la personne qui décroche ouvre. Écrivez le nom <strong>exact</strong> de
              l’entrée, comme dans UniFi Access › Concepteur d’interfaces : un nom faux rend le transfert impossible (alerte dans Gestion).
            </p>
            <Champ libelle="Entrée de test" valeur={rep.test} desactive={desactive} erreur={erreurs['repertoire.test']}
              aide={rep.test.trim()
                ? `Essais en cours : TOUS les transferts vont vers « ${rep.test.trim()} ». Videz ce champ pour passer en service.`
                : 'Vide : chaque appel va vers l’entrée de son motif ou de son équipe. Rempli : tous les transferts vont vers cette entrée (essais).'}
              alerte={!!rep.test.trim()} onChange={(v) => majRep({ test: v })} />
            <fieldset className="ip-groupe">
              <legend className="ip-groupe__titre">Par motif (prioritaire)</legend>
              <p className="lc-hint">Vide : l’entrée de l’équipe ci-dessous. Un client n’est jamais transféré : Sacha lui répond elle-même.</p>
              <div className="ip-deux">
                {ORDRE_TYPES_TRANSFERT.map((t: TypeTransfert) => (
                  <Champ key={t} libelle={TYPES[t] ?? t} valeur={rep.par_type[t]} desactive={desactive} erreur={erreurs[`repertoire.type.${t}`]}
                    placeholder="Selon l’équipe" onChange={(v) => majRep({ par_type: { ...rep.par_type, [t]: v } })} />
                ))}
              </div>
            </fieldset>
            <fieldset className="ip-groupe">
              <legend className="ip-groupe__titre">Par équipe</legend>
              <div className="ip-deux">
                {ORDRE_EQUIPES.map((e) => (
                  <Champ key={e} libelle={EQUIPES[e]} valeur={rep.par_equipe[e]} desactive={desactive} erreur={erreurs[`repertoire.equipe.${e}`]}
                    onChange={(v) => majRep({ par_equipe: { ...rep.par_equipe, [e]: v } })} />
                ))}
              </div>
            </fieldset>
          </div>
        </section>
      ) : null}

      <section className="lc-panel ip-section" aria-labelledby="ip-routage">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-routage"><Phone size={16} aria-hidden="true" /> {rep ? 'Téléphones SIP (appels de test)' : 'Qui faire sonner'}</h2></header>
        <div className="lc-panel__body ip-reglages">
          <p className="lc-hint" id="ip-routage-aide">
            {rep ? 'Seulement pour les appels passés depuis un téléphone de test : l’interphone de la porte, lui, fait sonner son Répertoire (ci-dessus). ' : ''}
            Quand Sacha prévient une équipe, tous les téléphones cochés sonnent en même temps ({BORNES.telephones} au plus par équipe).
            Ce sont les comptes du standard (ex. « tel-hugo »), pas des numéros.
          </p>
          {telephones.length === 0 ? <p className="ip-gris">Le standard ne déclare aucun téléphone pour l’instant.</p> : null}
          {ORDRE_EQUIPES.map((e) => (
            <fieldset key={e} className="ip-groupe" aria-describedby={`ip-routage-aide${erreurs[`routage.${e}`] ? ` ip-err-routage-${e}` : ''}`}>
              <legend className="ip-groupe__titre">{EQUIPES[e]}</legend>
              <Telephones choix={choixTelephones(telephones, s.routage[e])} choisis={s.routage[e]} desactive={desactive}
                onChange={(tel, oui) => majEquipe(e, tel, oui)} nom={EQUIPES[e]} />
              {erreurs[`routage.${e}`] ? <span className="lc-helper lc-helper--error" id={`ip-err-routage-${e}`} role="alert">{erreurs[`routage.${e}`]}</span> : null}
            </fieldset>
          ))}
        </div>
      </section>

      <section className="lc-panel ip-section" aria-labelledby="ip-secours">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-secours"><PhoneForwarded size={16} aria-hidden="true" /> Téléphones de secours</h2></header>
        <div className="lc-panel__body ip-reglages">
          <fieldset className="ip-groupe" aria-describedby={`ip-secours-aide${erreurs['secours'] ? ' ip-err-secours' : ''}`}>
            <legend className="ip-groupe__titre">Secours</legend>
            <p className="lc-hint" id="ip-secours-aide">
              {rep
                ? 'Si Sacha ne répond pas à un téléphone de test (PC éteint, panne), l’appel part vers ces téléphones. L’interphone de la porte joue alors le message « Répertoire ».'
                : 'Si Sacha ne répond pas (PC éteint, panne), l’appel part directement vers ces téléphones.'}
            </p>
            <Telephones choix={choixTelephones(telephones, s.secours)} choisis={s.secours} desactive={desactive} nom="Secours"
              onChange={(tel, oui) => setS((x) => ({ ...x, secours: basculer(x.secours, tel, oui) }))} />
            {erreurs['secours'] ? <span className="lc-helper lc-helper--error" id="ip-err-secours" role="alert">{erreurs['secours']}</span> : null}
          </fieldset>
        </div>
      </section>

      <section className="lc-panel ip-section" aria-labelledby="ip-noms">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-noms"><Truck size={16} aria-hidden="true" /> Livraisons reconnues</h2></header>
        <div className="lc-panel__body ip-reglages">
          <p className="lc-hint">Un nom par ligne. Leur nom suffit : Sacha ne pose aucune question et prévient la bonne équipe.</p>
          <div className="ip-deux">
            <Zone libelle="Transporteurs (colis → Technique)" aide={`${lignes(s.transporteurs).length} / ${BORNES.noms} noms`}
              erreur={erreurs['transporteurs']} lignesVisibles={8} desactive={desactive} valeur={s.transporteurs}
              onChange={(v) => maj({ transporteurs: v })} />
            <Zone libelle="Fournisseurs du bar (boissons → Bar)" aide={`${lignes(s.fournisseurs_bar).length} / ${BORNES.noms} noms`}
              erreur={erreurs['fournisseurs_bar']} lignesVisibles={8} desactive={desactive} valeur={s.fournisseurs_bar}
              onChange={(v) => maj({ fournisseurs_bar: v })} />
          </div>
        </div>
      </section>

      {s.equipe_connue !== null ? (
        <section className="lc-panel ip-section" aria-labelledby="ip-personnel">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-personnel"><Users size={16} aria-hidden="true" /> Personnel du club</h2></header>
          <div className="lc-panel__body">
            <SwitchRow checked={s.equipe_connue} disabled={desactive} onChange={(v) => maj({ equipe_connue: v })}
              title="Sacha connaît l’équipe"
              desc={s.equipe_connue
                ? 'Sacha reçoit les noms du personnel actif de Gestion, leur équipe (RH › Organisation) et qui a pointé dans Skello. Un employé qui donne son prénom est prévenu sans questions ; « je viens voir Yannick » fait sonner l’équipe de Yannick (la direction s’il n’est rangé dans aucune équipe). Sacha ne dit jamais à un visiteur qui est là ou non.'
                : 'Sacha ne connaît aucun nom : un employé qui sonne répond à ses questions, et une visite va à la direction.'} />
          </div>
        </section>
      ) : null}

      {s.voisins !== null ? (
        <section className="lc-panel ip-section" aria-labelledby="ip-voisins">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-voisins"><MapPin size={16} aria-hidden="true" /> Voisins</h2></header>
          <div className="lc-panel__body ip-reglages">
            <p className="lc-hint">Les voisins qu’on confond avec la porte du personnel. Sacha dit la phrase au visiteur et termine, sans déranger personne. {s.voisins.length} / {BORNES.voisins}.</p>
            {s.voisins.map((v, i) => (
              <div className="ip-deux" key={i}>
                <Champ libelle={`Voisin ${i + 1}`} valeur={v.nom} desactive={desactive} erreur={undefined} maxLength={BORNES.voisin_nom + 10}
                  placeholder="Centre des finances publiques"
                  onChange={(x) => setS((y) => ({ ...y, voisins: (y.voisins ?? []).map((w, j) => (j === i ? { ...w, nom: x } : w)) }))} />
                <div className="lc-field">
                  <Champ libelle="Ce que Sacha dit" valeur={v.phrase} desactive={desactive} erreur={undefined} maxLength={BORNES.voisin_phrase + 20}
                    placeholder="Ici, c’est le Live Club. Leur entrée est sur la place du Colombier."
                    onChange={(x) => setS((y) => ({ ...y, voisins: (y.voisins ?? []).map((w, j) => (j === i ? { ...w, phrase: x } : w)) }))} />
                  <button type="button" className="lc-btn lc-btn--ghost" disabled={desactive}
                    onClick={() => setS((y) => ({ ...y, voisins: (y.voisins ?? []).filter((_, j) => j !== i) }))}>
                    <Trash2 size={14} aria-hidden="true" /> Retirer {v.nom.trim() || 'ce voisin'}
                  </button>
                </div>
              </div>
            ))}
            {erreurs['voisins'] ? <span className="lc-helper lc-helper--error" role="alert">{erreurs['voisins']}</span> : null}
            <div>
              <button type="button" className="lc-btn lc-btn--ghost" disabled={desactive || s.voisins.length >= BORNES.voisins}
                onClick={() => setS((y) => ({ ...y, voisins: [...(y.voisins ?? []), { nom: '', phrase: '' }] }))}>
                <Plus size={14} aria-hidden="true" /> Ajouter un voisin
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {s.relance !== null ? (
        <section className="lc-panel ip-section" aria-labelledby="ip-relance">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-relance"><PhoneMissed size={16} aria-hidden="true" /> Si personne ne répond</h2></header>
          <div className="lc-panel__body ip-reglages">
            <Champ libelle="Relancer une fois vers l’entrée du Répertoire" valeur={s.relance} desactive={desactive} erreur={erreurs['relance']}
              placeholder="4.Administration"
              aide="Après chaque sonnerie, le service lit le journal d’UniFi Access : qui a ouvert, en combien de temps (visible dans la fiche de l’appel). Si l’appel est manqué, l’interphone sonne une seule fois sur cette entrée. Vide = pas de relance. Nom EXACT de l’entrée dans Access."
              onChange={(v) => maj({ relance: v })} />
          </div>
        </section>
      ) : null}

      <section className="lc-panel ip-section" aria-labelledby="ip-accueils">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-accueils"><MessageCircle size={16} aria-hidden="true" /> Phrases d’accueil</h2></header>
        <div className="lc-panel__body ip-reglages">
          <p className="lc-hint">Une phrase par ligne ; Sacha en choisit une au hasard. Au moins une par moment, {BORNES.phrase} caractères au plus.</p>
          {MOMENTS.map((m) => (
            <Zone key={m.cle} libelle={m.libelle} aide={`${m.aide} ${lignes(s.accueils[m.cle]).length} / ${BORNES.phrases} phrases.`}
              erreur={erreurs[`accueils.${m.cle}`]} lignesVisibles={3} desactive={desactive} valeur={s.accueils[m.cle]}
              onChange={(v) => setS((x) => ({ ...x, accueils: { ...x.accueils, [m.cle]: v } }))} />
          ))}
        </div>
      </section>

      <section className="lc-panel ip-section" aria-labelledby="ip-infos">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-infos"><Info size={16} aria-hidden="true" /> Infos manuelles</h2></header>
        <div className="lc-panel__body ip-reglages">
          <Zone libelle="Ce que Sacha dit quand Gestion ne connaît pas de soirée ce soir"
            aide={`${s.infos_manuelles.trim().length} / ${BORNES.infos} caractères`} erreur={erreurs['infos_manuelles']}
            lignesVisibles={3} desactive={desactive} valeur={s.infos_manuelles} onChange={(v) => maj({ infos_manuelles: v })} />
        </div>
      </section>

      {s.budget_mensuel_usd !== null ? (
        <section className="lc-panel ip-section" aria-labelledby="ip-budget">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="ip-budget"><Wallet size={16} aria-hidden="true" /> Budget de l’IA</h2></header>
          <div className="lc-panel__body ip-reglages">
            <Champ libelle="Budget mensuel (dollars)" valeur={s.budget_mensuel_usd} desactive={desactive} erreur={erreurs['budget_mensuel_usd']}
              inputMode="numeric" maxLength={4} placeholder="40"
              aide="Coût estimé du mois (Claude pour réfléchir, Mistral pour écouter et parler) au-delà duquel une alerte part, une fois par mois. 0 = pas d’alerte. Le suivi est dans Sécurité › Interphone IA ; la facture des fournisseurs fait foi."
              onChange={(v) => maj({ budget_mensuel_usd: v })} />
          </div>
        </section>
      ) : null}
    </>
  )
}

/** Un champ d'une ligne (nom d'entrée du Répertoire), relié à son aide et à sa raison d'invalidité. */
function Champ(props: {
  libelle: string; valeur: string; desactive: boolean; erreur: string | undefined
  aide?: string; alerte?: boolean; placeholder?: string; onChange: (v: string) => void
  inputMode?: 'numeric'; maxLength?: number
}) {
  const id = useId()
  const decrit = [props.aide ? `${id}-aide` : '', props.erreur ? `${id}-err` : ''].filter(Boolean).join(' ')
  return (
    <div className="lc-field">
      <label className="lc-label" htmlFor={`${id}-champ`}>{props.libelle}</label>
      <input className="lc-input" id={`${id}-champ`} type="text" maxLength={props.maxLength ?? BORNES.entree + 20} disabled={props.desactive}
        inputMode={props.inputMode}
        value={props.valeur} placeholder={props.placeholder} autoComplete="off" spellCheck={false}
        aria-invalid={props.erreur ? true : undefined} aria-describedby={decrit || undefined}
        onChange={(ev) => props.onChange(ev.target.value)} />
      {props.aide ? <span className={`lc-helper${props.alerte ? ' ip-helper--alerte' : ''}`} id={`${id}-aide`}>{props.aide}</span> : null}
      {props.erreur ? <span className="lc-helper lc-helper--error" id={`${id}-err`} role="alert">{props.erreur}</span> : null}
    </div>
  )
}

/** Une zone de texte « une par ligne », reliée à son aide et à sa raison d'invalidité. */
function Zone(props: {
  libelle: string; aide: string; erreur: string | undefined; lignesVisibles: number; desactive: boolean
  valeur: string; onChange: (v: string) => void
}) {
  const id = useId()
  return (
    <div className="lc-field">
      <label className="lc-label" htmlFor={`${id}-zone`}>{props.libelle}</label>
      <textarea className="lc-input ip-zone" id={`${id}-zone`} rows={props.lignesVisibles} disabled={props.desactive} value={props.valeur}
        aria-invalid={props.erreur ? true : undefined}
        aria-describedby={`${id}-aide${props.erreur ? ` ${id}-err` : ''}`}
        onChange={(ev) => props.onChange(ev.target.value)} />
      <span className="lc-helper" id={`${id}-aide`}>{props.aide}</span>
      {props.erreur ? <span className="lc-helper lc-helper--error" id={`${id}-err`} role="alert">{props.erreur}</span> : null}
    </div>
  )
}

function Telephones(props: {
  choix: { tel: string; inconnu: boolean }[]
  choisis: string[]
  desactive: boolean
  nom: string
  onChange: (tel: string, oui: boolean) => void
}) {
  if (props.choix.length === 0) return <p className="lc-hint">Aucun téléphone à proposer.</p>
  return (
    <div className="ip-tels">
      {props.choix.map((c) => (
        <Checkbox key={c.tel} checked={props.choisis.includes(c.tel)} disabled={props.desactive}
          ariaLabel={`${props.nom} : ${c.tel}`}
          label={c.inconnu ? `${c.tel} (absent du standard)` : c.tel}
          onChange={(v) => props.onChange(c.tel, v)} />
      ))}
    </div>
  )
}
